import {
  addDisplayName,
  addHandlers,
  addLayoutEffect,
  addProps,
  addRef,
  addState,
  flowMax,
} from 'ad-hok'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { UserListPublic } from 'shared/src/api/types'
import { addNavigate } from 'shared/src/utils/addNavigate'
import {
  getDateDate,
  getDateMonth,
  getDateYear,
} from 'shared/src/utils/formatting'
import { typedAs } from 'shared/src/utils/typedAs'

import privateIcon from '../../assets/private-icon.svg'
import { makeUserEditUrl } from 'utils/routes'
import { serifFontFamily } from 'utils/styles'

const MAX_TEXT_HEIGHT = 96
const MAX_TEXT_WIDTH = 148

interface StyleProps {
  fontSize: number
}

const useStyles = createUseStyles({
  container: {
    flex: 1,
    flexBasis: '50%',
    width: 164,
    maxWidth: 164,
    height: 218,
    maxHeight: 218,
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editNameContainer: {
    zIndex: 2,
    position: 'absolute',
    left: 8,
    paddingRight: 8,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    maxWidth: MAX_TEXT_WIDTH,
    maxHeight: MAX_TEXT_HEIGHT,
  },
  editName: {
    color: '#fff',
    fontFamily: serifFontFamily,
    fontStyle: 'italic',
    textTransform: 'lowercase',
    fontSize: ({ fontSize }: StyleProps) => fontSize,
    fontWeight: 800,
    lineHeight: '90%',
    letterSpacing: 0.4,
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    paddingBottom: 2,
  },
  date: {
    zIndex: 2,
    position: 'absolute',
    left: 12,
    top: 0,
    display: 'flex',
    flexDirection: 'column',
    color: '#EFEFEF',
    lineHeight: '120%',
    fontWeight: 700,
    fontSize: 12,
    fontFamily: serifFontFamily,
    fontVariantNumeric: 'lining-nums',
  },
  coverImage: {
    maxWidth: 136,
    height: 182,
    width: 'auto',
    position: 'absolute',
    filter: 'brightness(0.5)',
    zIndex: 1,
    objectFit: 'cover',
  },
  productImage: {
    position: 'absolute',
    width: '100%',
    height: 'auto',
    maxWidth: 127,
    filter: 'brightness(0.5)',
    opacity: 1,
  },
  firstProductImage0: {
    left: 0,
    top: 27,
  },
  firstProductImage1: {
    left: 8,
    top: 0,
  },
  firstProductImage2: {
    right: 0,
    bottom: 0,
  },
  secondProductImage0: {
    left: 0,
    bottom: 0,
  },
  secondProductImage1: {
    left: 16,
    top: 0,
  },
  secondProductImage2: {
    right: 0,
    bottom: 16,
  },
  thirdProductImage0: {
    left: 0,
    top: 0,
  },
  thirdProductImage1: {
    left: 16,
    bottom: 8,
  },
  thirdProductImage2: {
    right: 0,
    bottom: 0,
  },
  privateIconContainer: {
    height: 20,
    width: 20,
    borderRadius: 4,
    backgroundColor: '#161616',
    position: 'absolute',
    right: 8,
    top: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  privateIcon: {
    color: '#FFF',
    width: 12,
    height: 12,
  },
})

const isOverflowing = (element: HTMLDivElement) =>
  element.scrollHeight > MAX_TEXT_HEIGHT || element.scrollWidth > MAX_TEXT_WIDTH

interface Props {
  list: UserListPublic
  stackIndex: number
  className?: string
}

export const EditStack: FC<Props> = flowMax(
  addDisplayName('EditStack'),
  addNavigate,
  addHandlers({
    onClickEdit:
      ({ list, navigate }) =>
      () => {
        if (list.user.username) {
          navigate(makeUserEditUrl(list.id, list.user.username), {
            state: {
              showBackButton: true,
            },
          })
        }
      },
  }),
  addRef('containerRef', typedAs<HTMLDivElement | null>(null)),
  addState('fontSize', 'setFontSize', 40),
  addLayoutEffect(
    ({ fontSize, setFontSize, containerRef }) =>
      () => {
        if (!containerRef.current || containerRef.current.scrollHeight === 0) {
          setFontSize((fontSize -= 2))
        } else if (isOverflowing(containerRef.current) && fontSize > 24) {
          setFontSize(fontSize - 2)
        }
      },
    ['fontSize', 'edit']
  ),
  addProps(({ fontSize }) => ({
    classes: useStyles({ fontSize }),
  })),
  addProps(({ classes }) => ({
    productClasses: [
      [
        classes.firstProductImage0,
        classes.firstProductImage1,
        classes.firstProductImage2,
      ],
      [
        classes.secondProductImage0,
        classes.secondProductImage1,
        classes.secondProductImage2,
      ],
      [
        classes.thirdProductImage0,
        classes.thirdProductImage1,
        classes.thirdProductImage2,
      ],
    ],
  })),
  ({
    list,
    onClickEdit,
    stackIndex,
    productClasses,
    containerRef,
    classes,
    className,
  }) => (
    <div onClick={onClickEdit} className={cx(className, classes.container)}>
      {list.products
        .slice(
          0,
          Math.min(list.edit?.coverImage ? 2 : 3, list.products.length - 1)
        )
        .map((product, productIndex) => (
          <img
            src={product.image.thumbnail}
            key={productIndex}
            className={cx(
              classes.productImage,
              productClasses[stackIndex % 3][productIndex]
            )}
            alt=""
          />
        ))}
      {list.edit?.coverImage && (
        <img
          src={list.edit?.coverImage.thumbnail}
          className={cx(productClasses[stackIndex % 3][2], classes.coverImage)}
          alt=""
        />
      )}
      <div className={classes.editNameContainer}>
        <span ref={containerRef} className={classes.editName}>
          {list.edit?.name ?? list.name}
        </span>
      </div>
      <div className={classes.date}>
        <span>{getDateMonth(list.createdAt)}</span>
        <span>{getDateDate(list.createdAt)}</span>
        <span>{getDateYear(list.createdAt)}</span>
      </div>
      {!list.isPublic && (
        <div className={classes.privateIconContainer}>
          <img src={privateIcon} alt="" className={classes.privateIcon} />
        </div>
      )}
    </div>
  )
)
